import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let mounted = true;
    const MAX_ATTEMPTS = 12; // ~24 seconds

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!mounted) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "failed" || data.status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (e) {
        // 404 = record not yet created; keep polling
        console.debug("BillingSuccess: status not ready yet", e?.message || e);
      }
      setAttempt((a) => {
        const next = a + 1;
        if (next >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return next;
        }
        setTimeout(poll, 2000);
        return next;
      });
    };
    poll();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const goDashboard = () => navigate("/");

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center px-6">
      <div data-testid="billing-success-card" className="max-w-md w-full bg-white border border-[#E5E4E0] p-10 text-center">
        {status === "polling" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-[#F0EFEA] flex items-center justify-center animate-pulse">
              <Clock size={28} className="text-[#5C5B5A]" />
            </div>
            <h1 className="font-display text-3xl font-light mt-4">Attivazione in corso…</h1>
            <p className="text-sm text-[#5C5B5A] mt-3">
              Stiamo confermando il tuo abbonamento con Stripe. Non chiudere questa pagina.
            </p>
            <div className="mt-4 text-xs font-mono text-[#8C8B8A]">Tentativo {attempt + 1}/12</div>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-[#2C4C3B] flex items-center justify-center">
              <CheckCircle2 size={28} className="text-white" />
            </div>
            <h1 className="font-display text-3xl font-light mt-4">Benvenuto!</h1>
            <p className="text-sm text-[#5C5B5A] mt-3">
              Il tuo abbonamento è attivo. Hai 7 giorni gratuiti — nessun addebito nei prossimi giorni.
            </p>
            <div className="mt-8 flex flex-col gap-3">
              <Button
                data-testid="go-dashboard-btn"
                onClick={goDashboard}
                className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em]"
              >
                Vai al pannello di controllo
              </Button>
              <Button
                onClick={() => navigate("/billing")}
                variant="outline"
                className="w-full border-[#8C8B8A] text-[#5C5B5A] font-mono uppercase text-xs tracking-[0.2em]"
              >
                Vedi il piano
              </Button>
            </div>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-[#C85A32] flex items-center justify-center">
              <XCircle size={28} className="text-white" />
            </div>
            <h1 className="font-display text-3xl font-light mt-4">
              {status === "timeout" ? "Attivazione lenta" : "Pagamento non riuscito"}
            </h1>
            <p className="text-sm text-[#5C5B5A] mt-3">
              {status === "timeout"
                ? "L'operazione sta impiegando più del previsto. Vai al tuo pannello: se l'abbonamento è già attivo lo vedrai lì."
                : "Non siamo riusciti a completare l'iscrizione. Riprova o contatta il supporto."}
            </p>
            <div className="mt-8">
              <Button
                onClick={() => navigate("/billing")}
                className="w-full bg-[#1C1B1A] hover:bg-[#000] text-white font-mono uppercase text-xs tracking-[0.2em]"
              >
                Vai al piano
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
